import { Layout } from "providers/Layout";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import {
  TimeLine,
  TimeLineItem
} from "../rent-reporting-upload/components/TimeLine";

import { cx } from "shared/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { Card } from "../rent-reporting-upload/components/Card";
import { useAuthContext } from "providers/Auth";
import { useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Loader } from "shared/components/Loader";
import { CsvUploadStatusResponse } from "providers/ProgressContext/slices/UploadCsvSlice/types";
import { Check, DownloadCloud01 } from "untitledui-js-base";
import { useEffect, useMemo, useRef } from "react";
import { Button, ButtonSize } from "shared/components";
import moment from "moment";
import { Redirect } from "shared/components/Redirect";
import { FEATURES } from "types/landlord/partner";

export const RentReportingUploadIdView = () => {
  const { type, permissions, partner } = useAuthContext();
  const hasIdentityTheftProtection = partner?.features.includes(
    FEATURES.ENABLE_IDENTITY_THEFT_PROTECTION
  );
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const printTriggered = useRef(false);

  const csvMeta = useQuery({
    queryKey: ["csvMeta", params.id],
    queryFn: () =>
      instance
        .get<CsvUploadStatusResponse>(`/portal/v1/csv/${params.id}`)
        .then(({ data }) => data)
  });

  useEffect(() => {
    if (location.state?.print && csvMeta.data && !printTriggered.current) {
      printTriggered.current = true;
      window.print();
    }

    const listener = () => {
      navigate(`/rent-reporting/upload/${params.id}`);
    };
    window.addEventListener("afterprint", listener);
    return () => {
      window.removeEventListener("afterprint", listener);
    };
  }, [csvMeta.data]);

  const actionsCount = useMemo(() => {
    return Object.entries(csvMeta.data?.stats || {}).reduce(
      (actionCount, [key, value]) => {
        if (key === "valid_count" || key === "invalid_count")
          return actionCount;

        if (
          ["enroll", "unenroll", "verify", "data_update", "move_out"].includes(
            key
          ) &&
          typeof value === "number"
        )
          return actionCount + value;
        else return actionCount;
      },
      0
    );
  }, [csvMeta.data?.stats]);

  if (!permissions.canSubscribeRenters) return <Redirect href="/" />;

  if (type.isPMS && !type.isBoomOps) return null;
  if (csvMeta.isError)
    return (
      <Layout>
        <h1>Not found</h1>
      </Layout>
    );

  if (csvMeta.isLoading)
    return (
      <Layout
        headerClassName="pt-3"
        header={
          <div className="border-b border-alpha-100">
            <div className="mb-3 ml-6">
              <BreadCrumbs
                crumbs={[
                  { title: "Rent reporting", href: "/rent-reporting" },
                  { title: "Upload CSV", href: "/rent-reporting-upload" },
                  {
                    title: "...",
                    href: "/rent-reporting-upload-id"
                  }
                ]}
              />
            </div>
          </div>
        }
      >
        <div className="animate-appear mx-auto max-w-[760px]">
          <Card className="h-full p-8">
            <Loader />
          </Card>
        </div>
      </Layout>
    );

  const data = csvMeta.data;
  //   To satisfy ts
  if (!data) return null;

  return (
    <Layout
      headerClassName="pt-3"
      header={
        <div className="border-b border-alpha-100 no-print">
          <div className="mb-3 ml-6">
            <BreadCrumbs
              crumbs={[
                { title: "Rent reporting", href: "/rent-reporting" },
                { title: "Upload CSV", href: "/rent-reporting/upload" },
                {
                  title: data.csv_filename || "...",
                  href: `/rent-reporting/upload/${params.id}`
                }
              ]}
            />
          </div>
        </div>
      }
    >
      <div className="animate-appear mx-auto max-w-[760px]">
        <Card className="h-full p-8 print:border-none print:shadow-none">
          <div className="border-b border-alpha-40 pb-6">
            <h1 className="text-xl font-bold text-content-1000">
              {data.csv_filename || "renters_csv.csv"}
            </h1>
          </div>
          <div className="py-6 border-b border-alpha-40 mb-8 flex items-center justify-between">
            <div>
              <p className="text-xs text-content-400 mb-2">
                {data.csv_filename || "renters_csv.csv"}
              </p>
              <p className="text-sm font-medium text-content-1000">
                {moment(data.updated_at).format("MMMM Do YYYY, h:mm:ss a")}
              </p>
            </div>

            <a download href={data.result_csv_url} className="block">
              <Button
                Icon={DownloadCloud01}
                size={ButtonSize.SM}
                className="no-print"
              >
                Download CSV analysis
              </Button>
            </a>
          </div>
          <div className="flex items-center">
            <div className="flex h-5 w-5 -translate-x-px select-none items-center justify-center rounded-full bg-success-500 text-center text-sm font-bold text-white">
              <Check className="stroke-white stroke-[2px]" size="14px" />
            </div>
            <h3 className="text-base font-bold text-content-700 pl-2">
              Actions
            </h3>
          </div>

          {hasIdentityTheftProtection ? (
            <p className="my-2 text-base font-medium text-content-700">
              Rent reporting
            </p>
          ) : null}
          <TimeLine>
            {type.isManual ||
            (type.isPMS && type.isBoomOps) ||
            (type.isPMS &&
              type.canManageEntitiesDirectly &&
              data.options.enroll) ? (
              <TimeLineItem
                wrapperClassName="my-5 items-center"
                childrenClassName="w-full text-sm font-medium gap-2"
              >
                <div
                  className={cx(
                    "flex items-center",
                    data.stats.enroll === 0
                      ? "text-content-100"
                      : "text-content-1000"
                  )}
                >
                  <Tooltip>
                    <TooltipTrigger>
                      <span className="-ml-1.5 text-sm text-inherit">
                        Subscribe renters
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[300px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-white">
                      <p className="font-bold">Subscribe renters</p>
                      <p>
                        You can specify whether you want to subscribe renters
                        who are listed in the file
                      </p>
                    </TooltipContent>
                  </Tooltip>
                  <span
                    className={cx(
                      "ml-auto block text-right",
                      data.stats.enroll === 0
                        ? "text-content-100"
                        : "text-content-300"
                    )}
                  >
                    {data.stats.enroll}
                  </span>
                </div>
              </TimeLineItem>
            ) : null}

            {data.options.unenroll ? (
              <TimeLineItem
                wrapperClassName="my-5 items-start"
                childrenClassName="w-full text-sm font-medium gap-2"
              >
                <div
                  className={cx(
                    "flex items-center",
                    data.stats.unenroll === 0
                      ? "text-content-100"
                      : "text-content-1000"
                  )}
                >
                  <Tooltip>
                    <TooltipTrigger>
                      <span className="-ml-1.5 text-sm text-inherit">
                        Unsubscribe renters
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[300px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-white">
                      <p className="font-bold">Unsubscribe renters</p>
                      <p>
                        You can specify whether you want to unsubscribe renters
                        who are missing in the file
                      </p>
                    </TooltipContent>
                  </Tooltip>
                  <span
                    className={cx(
                      "ml-auto block text-right",
                      data.stats.unenroll === 0
                        ? "text-content-100"
                        : "text-content-300"
                    )}
                  >
                    {data.stats.unenroll}
                  </span>
                </div>
              </TimeLineItem>
            ) : null}

            {data.options.verify ? (
              <TimeLineItem
                wrapperClassName="my-5 items-center"
                childrenClassName="w-full text-sm font-medium gap-2"
              >
                <div
                  className={cx(
                    "flex items-center",
                    data.stats.verify === 0
                      ? "text-content-100"
                      : "text-content-1000"
                  )}
                >
                  <Tooltip>
                    <TooltipTrigger>
                      <span className="-ml-1.5 text-sm text-inherit">
                        Verify payments
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[300px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-white">
                      <p className="font-bold">Verify payments</p>
                      <p>
                        You can specify whether you want report rent for renters
                        listed in the file
                      </p>

                      <ul className="list-disc pl-4 mt-4">
                        <li className="mb-2">
                          Outstanding balance will be used to verify rent
                          payments
                        </li>
                        <li className="mb-2">
                          Past months will be reported as &apos;No Data&apos; if
                          you don&apos;t report rent payments within 30 day
                        </li>
                        <li>
                          New subscribers payments will be reported after their
                          subscriptions are approved
                        </li>
                      </ul>
                    </TooltipContent>
                  </Tooltip>
                  <span
                    className={cx(
                      "ml-auto block text-right",
                      data.stats.verify === 0
                        ? "text-content-100"
                        : "text-content-300"
                    )}
                  >
                    {data.stats.verify}
                  </span>
                </div>
              </TimeLineItem>
            ) : null}

            {data.options.update ? (
              <TimeLineItem
                wrapperClassName="my-5 items-center"
                childrenClassName="w-full text-sm font-medium gap-2"
              >
                <div
                  className={cx(
                    "flex items-center",
                    data.stats.data_update === 0
                      ? "text-content-100"
                      : "text-content-1000"
                  )}
                >
                  <Tooltip>
                    <TooltipTrigger>
                      <span className="-ml-1.5 text-sm text-inherit">
                        Update tradeline data
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[300px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-white">
                      <p className="font-bold">Update tradeline data</p>
                      <p>Critical tradeline data update</p>
                    </TooltipContent>
                  </Tooltip>
                  <span
                    className={cx(
                      "ml-auto block text-right",
                      data.stats.data_update === 0
                        ? "text-content-100"
                        : "text-content-300"
                    )}
                  >
                    {data.stats.data_update}
                  </span>
                </div>
              </TimeLineItem>
            ) : null}

            {data.options.moveout ? (
              <TimeLineItem
                wrapperClassName="my-5 items-center"
                childrenClassName="w-full text-sm font-medium gap-2"
              >
                <div
                  className={cx(
                    "flex items-center",
                    data.stats.move_out === 0
                      ? "text-content-100"
                      : "text-content-1000"
                  )}
                >
                  <Tooltip>
                    <TooltipTrigger>
                      <span className="-ml-1.5 text-sm text-inherit">
                        Process move out
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[300px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-white">
                      <p className="font-bold">
                        Process move out (close tradeline)
                      </p>
                      <p>Close tradelines for renters</p>
                    </TooltipContent>
                  </Tooltip>
                  <span
                    className={cx(
                      "ml-auto block text-right",
                      data.stats.move_out === 0
                        ? "text-content-100"
                        : "text-content-300"
                    )}
                  >
                    {data.stats.move_out}
                  </span>
                </div>
              </TimeLineItem>
            ) : null}
          </TimeLine>

          {hasIdentityTheftProtection ? (
            <>
              <p className="my-2 text-base font-medium text-content-700">
                Identity theft protection
              </p>
              <TimeLine>
                <TimeLineItem
                  wrapperClassName="my-5 items-center"
                  childrenClassName="items-center flex w-full text-sm justify-between font-medium gap-2"
                  isLast
                >
                  <span className="-ml-1.5 text-sm text-content-1000">
                    Invitations sent
                  </span>
                  <span className="ml-auto block text-content-300">
                    {data.stats.enroll + data.stats.resubscribe}
                  </span>
                </TimeLineItem>
              </TimeLine>
            </>
          ) : null}
        </Card>
      </div>
    </Layout>
  );
};
